import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { NodeTypes } from 'services/definition/class/node-types';
import { DefinitionService } from 'services/definition/definition.service';
import { TelemetryDeliveryMethod, TelemetryType, TreeNodeType, } from "services/definition/enum";
import { IDeviceNode, ITelemetryNode, ITreeNode } from "services/definition/interface";
import { Logger } from 'src/app/common/utility/logger/logger';

@Component({
  selector: 'source-selection',
  templateUrl: './source-selection.component.html',
  styleUrls: ['./source-selection.component.scss']
})
export class SourceSelectionComponent implements OnInit {
  @Input() selectedNodes?: any[];
  @Input() allNodes!: any[];
  @Input() showAll: boolean = false;
  @Input() typeFilter: Array<TelemetryType> | undefined = undefined;
  @Input() deliveryFilter: Array<TelemetryDeliveryMethod> | undefined = undefined;
  @Input() editMode: boolean = true;
  @Input() editIcon: string = 'settings';
  @Input() searchStr: string = '';
  // Two-way bind node being edited
  @Input() editNode!: ITelemetryNode;
  @Output() editNodeChange = new EventEmitter<ITelemetryNode>();
  // Change Settings Request to Parent
  @Output() changeSettings = new EventEmitter();
  treeControl = new NestedTreeControl<ITreeNode>((node) => node.children);
  dataSource = new MatTreeNestedDataSource<ITreeNode>();

  constructor() {
  }

  public ngOnInit(): void {
    this.initTreeNodes();
    this.expandAllSources()
  } // ngOnInit()

  ngOnChanges(changes: SimpleChanges) {
    const allNodes = 'allNodes';
    const editMode = 'editMode'
    if (!!changes[allNodes]) {
      const change = changes[allNodes];
      if (change.previousValue && (change.previousValue !== change.currentValue)) {
        this.initTreeNodes();
      }
    }
    if (!!changes[editMode] && !this.showAll) {
      this.expandAllSources()
    }
  } // ngOnChanges()

  private expandAllSources(): void {
    const nodes = this.treeControl.dataNodes
    if (!nodes)
      return

    nodes.forEach(node => {
      this.expandSourceNodes(node)
    });
  } // expandAllSources()

  private expandSourceNodes(node: any): void {
    if (!node)
      return

    if (node.indeterminate || node.selected) {
      // expand the node
      this.treeControl.expand(node);
      if (node.children)
        node.children.forEach((node: any) => this.expandSourceNodes(node))
    }
  } // expandSourceNodes()

  // Initialize Tree Nodes
  private initTreeNodes(): void {
    this.buildFilteredTree(this.allNodes);

    // Merge the selectedNodes provided in the allNodes provided
    this.mergeSelectedToTree(this.selectedNodes, this.allNodes)

    // Setup the TreeControl & Tree DataSource
    this.dataSource.data = this.allNodes;
    this.treeControl.dataNodes = this.allNodes;

    // Initialize the parent nodes (child->parent)
    Object.keys(this.dataSource.data).forEach((key: any) => {
      this.setParent(this.dataSource.data[key], null);
    });
  } // initTreeNodes()

  // Trigger by selected Change Icon in Edit Mode
  changeNodeRequest(node: any) {
    this.editNodeChange.emit(node);
    this.changeSettings.emit();
  } // changeNode()

  private buildFilteredTree(tree: ITreeNode[]) {
    tree.forEach(node => {
      if (NodeTypes.getNodeType(node) !== TreeNodeType.Device) {
        if (node.children)
          this.buildFilteredTree(node.children)
      } else {
        node.children = DefinitionService.getDeviceChildrenFromLTMI(
          (<IDeviceNode>node).ltmi, this.typeFilter, this.deliveryFilter, undefined, (<IDeviceNode>node).arrays)
      }
    });
  } // buildFilteredTree()

  private mergeSelectedToTree(selected: ITreeNode[] | undefined, clean: ITreeNode[]) {
    if (selected == undefined)
      return;

    selected.forEach((node) => {
      // see if clean node has this node
      const found = clean.find((element) => node.name == element.name)

      if (node.children && (found && found.children)) {
        this.mergeSelectedToTree(node.children, found.children);
        // Flag the parents
        found.selected = found.children.every(child => child.selected);
        found.indeterminate = found.children.some(child => child.selected || child.indeterminate);
      } else { // Possibly Leaf
        if (!node.children && found) {
          found.selected = true;
          (<ITelemetryNode>found).settings = (<ITelemetryNode>node).settings
        } else { // Error/Mismatch
          // If Node is not found, then delete the node from selected
          selected.splice(selected.findIndex((val) => val.name === node.name), 1);
        } // if/else
      } // if/else
    }) // selected.forEach

  } // mergeSelectedToTree()

  public hasTreeNodeChild = (_: number, node: any) => {
    return !!node.children && node.children.length > 0;
  } // hasTreeNodeChild

  private setParent(node: any, parent: any) {
    node.parent = parent;
    if (node.children) {
      node.children.forEach((childNode: any) => {
        this.setParent(childNode, node);
      });
    }
  } // setParent()

  // Iterates up the tree and marks selected/indeterminate
  private checkAllParents(node: ITreeNode): void {
    if (node.parent) {
      const descendants = this.treeControl.getDescendants(node.parent);
      node.parent.selected = descendants.every(child => child.selected);
      node.parent.indeterminate = descendants.some(child => child.selected);
      this.checkAllParents(node.parent);
    }
  } // checkAllParents()

  // Utility to dump the content of a tree
  public static dumpTree(nodes: any[]): void {
    nodes.forEach((node) => {
      Logger.info(node)
      if (node.children) {
        this.dumpTree(node.children)
      }
    })
  } // dumpTree()

  // Returns a copy of only the selected/indeterminate items
  public static pruneTree(nodes: ITreeNode[]): Array<any> {
    const result: Array<any> = [];

    nodes.forEach((node) => {
      // node.parent = undefined;
      if (node.selected || node.indeterminate) {
        const tmp: ITreeNode = node;
        if (node.children != undefined && node.children.length > 0) {
          tmp.children = SourceSelectionComponent.pruneTree(node.children)
        }
        result.push(tmp)
      }
    })

    return result;
  } // pruneTree()

  public itemToggle(checked: boolean, node: any): void {
    node.selected = checked;
    if (node.children) {
      node.children.forEach((child: any) => {
        this.itemToggle(checked, child);
      });
    }
    this.checkAllParents(node);
  } // itemToggle()

  // TODO: [WEBPLAT-255] Debounce Tree Search feature
  public hideLeafNode(node: any): string {
    if (this.searchStr) {
      let searchResult = new RegExp(this.searchStr, 'i').test(node.displayName) === false;
      return ((!this.showAll && !node.selected) || searchResult) ? 'none' : '';
    }
    return (!this.showAll && !node.selected) ? 'none' : '';
  } // hideLeafNode()

  public hideParentNode(node: any): string {
    return (
      this.treeControl
        .getDescendants(node)
        .filter(node => node.children == null || node.children.length === 0)
        .every(node => this.hideLeafNode(node)) ||
        (!this.showAll && !(node.selected || node.indeterminate)) ? 'none' : ''
    );
  } // hideParentNode()

} // class SourceSelectionComponent
